import { ChevronDown, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VerificationEvidencePreview } from "../verification-evidence-preview/verification-evidence-preview";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useOpenVerificationEvidence } from "../license-verification/use-open-verification-evidence";
import type { LatestScreening, ScreeningScope } from "./types";

const OIG_DATASET_KEY = "leie-dataset";
// The screening's own summary file is identified by matching its result key
// (see `evidenceLabel` below), not listed here, since that key differs by
// source (e.g. "screening-summary" for SAM/OIG/OFAC, "state-xx" per state).
const EVIDENCE_LABELS: Record<string, string> = {
  [OIG_DATASET_KEY]: "LEIE dataset (JSON)",
  "leie-release": "Dataset release information (JSON)",
};

export function ScreeningEvidence({
  latest,
  scope,
  sourceLabel,
  program,
}: {
  latest: LatestScreening;
  scope: ScreeningScope;
  sourceLabel: string;
  program?: string;
}) {
  const stateSnapshot = latest.result?.facts.stateSnapshot;
  const oigSnapshot = latest.result?.facts.oigSnapshot;
  const samSnapshot = latest.result?.facts.samSnapshot;
  const snapshot =
    stateSnapshot ??
    oigSnapshot ??
    samSnapshot ??
    latest.result?.facts.sourceSnapshot;
  let snapshotLabel = "OFAC";
  if (stateSnapshot) {
    snapshotLabel = program ?? sourceLabel;
  } else if (oigSnapshot) {
    snapshotLabel = "OIG / LEIE";
  } else if (samSnapshot) {
    snapshotLabel = "SAM";
  }
  const openEvidence = useOpenVerificationEvidence({
    scope,
    runId: latest.runId,
    checkId: latest.checkId,
  });
  // New OIG runs retain a small dataset reference; the shared archive above
  // provides the full source data. Older runs still need their per-check file.
  const responses = latest.evidence.filter(
    (evidence) =>
      evidence.kind === "source-data" &&
      evidence.mediaType === "application/json" &&
      !(oigSnapshot && evidence.key === OIG_DATASET_KEY),
  );
  if (!responses.length && !snapshot) {
    return null;
  }
  // The summary file's key is the screening's own result key (e.g.
  // "screening-summary", or a state's "state-xx"), not a fixed constant.
  function evidenceLabel(evidence: (typeof responses)[number], index: number) {
    if (evidence.key === latest.result?.resultKey) {
      return "Screening summary (JSON)";
    }
    return (
      EVIDENCE_LABELS[evidence.key] ?? `Source response ${index + 1} (JSON)`
    );
  }
  return (
    <Collapsible className="space-y-2 border-t pt-2">
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="group h-auto gap-1 p-0 text-xs text-muted-foreground"
        >
          Audit records
          <ChevronDown
            className="size-3 transition-transform group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Original source data and the screening summary are retained for this
          check. These files contain technical data for audit or export.
        </p>
        <div className="flex flex-wrap gap-2">
          {snapshot && (
            <Button
              type="button"
              variant="surface"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                openEvidence.open({
                  snapshot: true,
                  label: `${snapshotLabel} source dataset`,
                  // The state check seals one snapshot per state; the API
                  // requires this id to pick this state's copy.
                  snapshotId: stateSnapshot?.id,
                })
              }
            >
              <Eye className="size-4" aria-hidden="true" />
              {`${snapshotLabel} source dataset`}
            </Button>
          )}
          {responses.map((evidence, index) => (
            <Button
              key={evidence.fileUploadId}
              type="button"
              variant="surface"
              size="sm"
              onClick={() =>
                openEvidence.open({
                  fileUploadId: evidence.fileUploadId,
                  label: evidenceLabel(evidence, index),
                })
              }
            >
              {evidenceLabel(evidence, index)}
            </Button>
          ))}
        </div>
      </CollapsibleContent>
      <VerificationEvidencePreview {...openEvidence} />
    </Collapsible>
  );
}
